// eslint-disable-next-line simple-import-sort/imports
import {
  DatesSetArg,
  EventClickArg,
  EventContentArg
} from "@fullcalendar/core"; // must go before plugins
import itLocale from "@fullcalendar/core/locales/it";
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import {
  Day,
  DayWithHolidaysForCal,
  GetVacationsPerMonthQueryVariables,
  UserForCalendar,
  getStartEndOfMonthExpanded,
  groupUsersByDay,
  useGetVacationsPerMonthQuery
} from "@mc-intranet/shared";
import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MyTheme,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { authStorageAtom, holydaysByYearsAtom } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import CalendarWrapper from "../../components/Calendar";

const Vacations = () => {
  const authStorage = useAtomValue(authStorageAtom);
  const { user } = authStorage;

  const isDesktop = useMediaQuery((theme: MyTheme) =>
    theme.breakpoints.up("md")
  );

  const [holidays, setHolidays] = useAtom(holydaysByYearsAtom);
  // const [holidays, setHolidays] = useState<any[]>(getHolidaysForCal(year));
  const [calData, setCalData] = useState<any[]>(Object.values(holidays).flat());
  const [modalVisible, setModalVisible] = useState(false);
  const [usersForInDay, setUsersForInDay] = useState<UserForCalendar[]>([]);
  const [userOnVacations, setUserOnVacations] = useState<
    { label: string; value: string }[]
  >([]);
  const [filteredUserOnVacations, setFilteredUserOnVacations] = useState<
    { label: string; value: string }[]
  >([]);

  const { client } = useGraphQLClient();
  const [variables, setVariables] =
    useState<GetVacationsPerMonthQueryVariables>({
      startDate: getStartEndOfMonthExpanded(moment()).startOfMonth,
      endDate: getStartEndOfMonthExpanded(moment()).endOfMonth
    });

  const { data, isLoading: loadingUsers } = useGetVacationsPerMonthQuery(
    client,
    variables,
    {
      enabled: !!user,
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  const dateChange = (arg: DatesSetArg) => {
    // console.log("datesSet", arg);
    // aggiorno le vacanze se cambia l'anno
    const start = moment(arg.start).year();
    const end = moment(arg.end).year();
    const isOverlapping = start !== end;
    if (isOverlapping) {
      // console.log("set isOverlapping");
      setHolidays([start, end]);
    } else {
      // console.log("set year");
      setHolidays(end);
    }

    setVariables((prev) => {
      return {
        ...prev,
        startDate: moment(arg.start).format("YYYY-MM-DD"),
        endDate: moment(arg.end).format("YYYY-MM-DD")
      };
    });
  };

  useEffect(() => {
    if (data) {
      const days = data.days;
      const { grouped, users } = groupUsersByDay(days as Day[]);
      const items = [];

      for (const [key, value] of Object.entries(grouped)) {
        console.log(`${key}: ${value}`);
        const _item: DayWithHolidaysForCal = {
          id: key,
          start: key,
          extendedProps: { users: value },
          backgroundColor: "transparent",
          borderColor: "transparent"
        };
        items.push(_item);
      }

      setUserOnVacations(users);
      setCalData([...Object.values(holidays).flat(), ...items]);
    }
  }, [data, holidays]);

  interface UsersInVacationProps {
    isModal?: boolean;
    users: UserForCalendar[];
  }

  const UsersInVacation: React.FC<UsersInVacationProps> = (props) => {
    const { isModal = false, users } = props;

    return (
      <Box
        className="users-in-vacation-cell"
        overflow="auto"
        display={isModal ? "inherit" : { xs: "flex", md: "inherit" }}
        flexWrap="wrap"
        p={{ xs: 0, md: 1 }}
        justifyContent="center"
        sx={{ pointerEvents: "visible" }}
      >
        {users &&
          users?.map((item) => {
            const firstName = item?.user?.firstName ?? "";
            const lastName = item?.user?.lastName ?? "";
            const color = item?.user?.color ?? "";

            // controllo i filtri
            const UserInVacation = () => {
              return (
                <Box
                  color="text.primary"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  margin={isModal ? 0 : { xs: "2px", md: 0 }}
                >
                  <Box
                    component="span"
                    bgcolor={color}
                    borderRadius="50%"
                    marginRight={isModal ? 1 : { xs: 0, md: 1 }}
                    width={isModal ? 8 : { xs: 5, md: 8 }}
                    height={isModal ? 8 : { xs: 5, md: 8 }}
                  />
                  <Typography
                    display={isModal ? "block" : { xs: "none", md: "block" }}
                    fontSize="13px"
                  >{`${firstName.charAt(0)}.${lastName}`}</Typography>
                </Box>
              );
            };

            if (filteredUserOnVacations.length === 0) {
              return <UserInVacation key={color} />;
            } else {
              let _color = "";

              filteredUserOnVacations.forEach((selected) => {
                if (selected.value === color) {
                  _color = color;
                  return;
                }
              });

              if (_color !== "") return <UserInVacation key={_color} />;
            }
            return null;
          })}
      </Box>
    );
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const { event } = eventContent;
    const { display, extendedProps } = event;
    const { users } = extendedProps;
    if (display === "background") return;
    // console.log(`users`, users);

    return <UsersInVacation users={users as UserForCalendar[]} />;
  };

  function handleUserChange(
    event: React.SyntheticEvent<Element, Event>,
    value: any
  ) {
    setFilteredUserOnVacations(value);
  }

  const onSelectDate = (arg: EventClickArg) => {
    // const date = moment(arg.dateStr);
    console.log("onSelectDate ", arg.event.extendedProps);
    setUsersForInDay(arg.event?.extendedProps["users"] as UserForCalendar[]);
    setModalVisible(true);
  };
  console.log("calData", calData);
  return (
    <Box pb={3}>
      <Box
        className="filters"
        display="flex"
        alignItems="center"
        justifyContent="center"
        paddingY={3}
        // sx={{ pointerEvents: { xs: "visible", md: "none" } }}
      >
        <Autocomplete
          multiple
          disablePortal
          id="combo-box-demo"
          sx={{
            bgcolor: "background.default",
            marginInline: 2,
            maxWidth: "100%",
            minWidth: 260
          }}
          size="small"
          options={userOnVacations}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Utente"
              placeholder="Filtra per utente"
            />
          )}
          renderTags={(value, getTagProps) => {
            return value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                sx={{ backgroundColor: option.value, color: "white" }}
                label={option.label}
              />
            ));
          }}
          onChange={handleUserChange}
        />
      </Box>
      <CalendarWrapper isLoading={loadingUsers}>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          locale={itLocale}
          initialView="dayGridMonth"
          events={calData}
          eventContent={renderEventContent}
          datesSet={dateChange}
          eventClick={onSelectDate}
          headerToolbar={{
            start: "title",
            center: "",
            end: "today prevYear,prev,next,nextYear"
          }}
        />
      </CalendarWrapper>
      {!isDesktop && modalVisible && (
        <Dialog
          open={modalVisible}
          onBackdropClick={() => setModalVisible(false)}
        >
          <DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => setModalVisible(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <Close />
            </IconButton>
            In Ferie
          </DialogTitle>
          <DialogContent sx={{ minWidth: "250px" }}>
            <UsersInVacation isModal users={usersForInDay} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default Vacations;
